import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption, ScheduleInput } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { createScheduleInput } from 'src/app/common-components/helpers/schedule-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { ScheduleDetailSaveModel } from '../../schedule-details/models/schedule-detail-save.model';
import { SaveSchedule, SaveScheduleVariables } from '../../schedule-details/queries/generated/SaveSchedule';
import { SaveScheduleMutation } from '../../schedule-details/queries/save-schedule.graphql';
import { SchedulesFilterOption } from '../models/schedule-filter-option';
import { ScheduleOrderingOptions, ScheduleRegisterOptions } from '../models/schedule-register-options';
import { getScheduleQuery } from '../queries/GetSchedules.graphql';
import { GetSchedule, GetScheduleVariables } from '../queries/generated/GetSchedule';

@Injectable({ providedIn: 'root' })
export class ScheduleRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getSchedule(
      endCursor: string | null,
      filters: SchedulesFilterOption[] | null,
      quickFilters: ScheduleRegisterOptions | null,
      orderingOptions: ScheduleOrderingOptions[] | null
   ): Observable<ExecutionResult<GetSchedule>> {
      const filterBuilderFilterExpression: FilterExpression = { filterOptions: [] };

      if (filters && filters.length > 0) {
         filterBuilderFilterExpression.filterOptions = filters.map(this.convertLocationFilterOption.bind(this));
      }

      if (quickFilters && quickFilters.searchText) {
         const searchWords = quickFilters.searchText.trim().split(/\s+/);
         const fieldsToSearch = ['Name', 'Description'];

         filterBuilderFilterExpression.subExpressions = searchWords.map((word) => {
            const wordSubExpression: FilterExpression = {
               filterOptions: fieldsToSearch.map((field) => {
                  return {
                     field: field,
                     operation: FilterOperations.Contains,
                     value: word,
                  };
               }),
               logicalOperator: LogicalOperators.Or,
            };

            return wordSubExpression;
         });

         filterBuilderFilterExpression.logicalOperator = LogicalOperators.And;
      }
      const variables = this.getScheduleVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetSchedule, GetScheduleVariables>({
         query: getScheduleQuery,
         variables,
      });
   }

   saveAs(scheduleDetail: ScheduleDetailSaveModel): Observable<ExecutionResult<SaveSchedule>> {
      const fields: (keyof ScheduleInput)[] = [
         'name',
         'description',
         'accountId',
         'scheduleDetails',
         'closedExceptions',
      ];
      const schedule = createScheduleInput(scheduleDetail, fields);
      return this.apiService.query<SaveSchedule, SaveScheduleVariables>({
         query: SaveScheduleMutation,
         variables: {
            deletedClosedExceptionIds: [],
            deletedScheduleHourIds: [],
            schedule,
         },
      });
   }

   private convertLocationFilterOption(option: SchedulesFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(filterOption: SchedulesFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (filterOption.operation === FilterOperations.HasValue) {
         return filterOption.value;
      }

      switch (filterOption.type) {
         case 'schedule':
            return Array.isArray(filterOption.value)
               ? filterOption.value.map((v) => {
                    return v.scheduleId;
                 })
               : filterOption.value?.scheduleId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return filterOption.value;
      }
   }

   private getScheduleVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetScheduleVariables {
      const variables: GetScheduleVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
